import React, { useMemo, useState } from "react";
import { AlertCircle, ChevronDown } from "react-feather";
import { StatusPill, type StatusVariant } from "../../atoms/StatusPill";
import { Pagination } from "../../molecules/Pagination";
import { Spinner } from "../../atoms/Spinner";
import { EmptyState } from "../../molecules/EmptyState";
import { PlatformIcon, FormatIcon } from "./icons";
import { DeliveryRowActionsMenu } from "./DeliveryRowActionsMenu";
import type {
  Delivery,
  DeliveryFormat,
  DeliveryStatus,
  DeliveryType,
  Platform,
} from "./types";
import {
  Card,
  CardHeader,
  CardTitle,
  IconButton,
  TableWrap,
  Table,
  Th,
  Td,
  Tr,
  PlatformCell,
  ChipRow,
  Chip,
  MoreLink,
  FormatCell,
  ActionCell,
} from "./ActiveDeliveriesTable.styles";

const platformLabel: Record<Platform, string> = {
  slack: "Slack",
  email: "Email",
  teams: "Teams",
};

const deliveryTypeLabel: Record<DeliveryType, string> = {
  scheduled: "Scheduled",
  alert: "Alert",
  immediate: "Immediate",
};

const formatLabel: Record<DeliveryFormat, string> = {
  presentation: "Presentation",
  pdf: "PDF",
  video: "Video",
  "image-set": "Image set",
};

const statusLabel: Record<DeliveryStatus, string> = {
  active: "Active",
  cancelled: "Cancelled",
  completed: "Completed",
  paused: "Paused",
};

const statusVariant: Record<DeliveryStatus, StatusVariant> = {
  active: "info",
  cancelled: "error",
  completed: "success",
  paused: "warning",
};

export interface ActiveDeliveriesTableProps {
  deliveries: Delivery[];
  totalCount?: number;
  /** True while the list is being fetched — renders a spinner row instead
   * of an empty table (which would otherwise flash "no deliveries yet"
   * before data arrives). */
  isLoading?: boolean;
  /** True when the list fetch failed — renders `errorMessage` instead of
   * the table body. */
  isError?: boolean;
  /** Backend's actual error string (via `getApiErrorMessage`) — always
   * rendered verbatim, never replaced with a generic message here. */
  errorMessage?: string | null;
  onEdit?: (id: string) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onCancel?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const TABLE_COLUMN_COUNT = 6;

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 25] as const;

const ActiveDeliveriesTable: React.FC<ActiveDeliveriesTableProps> = ({
  deliveries,
  totalCount,
  isLoading = false,
  isError = false,
  errorMessage,
  onEdit,
  onPause,
  onResume,
  onCancel,
  onDelete,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const total = totalCount ?? deliveries.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageRows = useMemo(
    () => deliveries.slice((page - 1) * pageSize, page * pageSize),
    [deliveries, page, pageSize],
  );
  const isEmpty = !isLoading && !isError && deliveries.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>All deliveries</CardTitle>
        <IconButton aria-label="Collapse">
          <ChevronDown size={20} style={{ transform: "rotate(180deg)" }} />
        </IconButton>
      </CardHeader>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Platform</Th>
              <Th>Destination</Th>
              <Th>Delivery type</Th>
              <Th>Format</Th>
              <Th>Status</Th>
              <Th>Action</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <Tr>
                <Td colSpan={TABLE_COLUMN_COUNT}>
                  <EmptyState icon={<Spinner />} caption="Loading deliveries…" />
                </Td>
              </Tr>
            )}
            {!isLoading && isError && (
              <Tr>
                <Td colSpan={TABLE_COLUMN_COUNT}>
                  <EmptyState
                    icon={<AlertCircle />}
                    caption={errorMessage || "Couldn't load deliveries."}
                  />
                </Td>
              </Tr>
            )}
            {isEmpty && (
              <Tr>
                <Td colSpan={TABLE_COLUMN_COUNT}>
                  <EmptyState caption="No deliveries yet — add one to get started." />
                </Td>
              </Tr>
            )}
            {!isLoading && !isError && pageRows.map((d) => (
              <Tr key={d.id}>
                <Td>
                  <PlatformCell>
                    <PlatformIcon platform={d.platform} />
                    {platformLabel[d.platform]}
                  </PlatformCell>
                </Td>
                <Td>
                  <ChipRow>
                    {d.destinations.map((dest) => (
                      <Chip key={dest}>{dest}</Chip>
                    ))}
                    {d.hiddenDestinationCount ? (
                      <MoreLink>+{d.hiddenDestinationCount} more</MoreLink>
                    ) : null}
                  </ChipRow>
                </Td>
                <Td>{deliveryTypeLabel[d.deliveryType]}</Td>
                <Td>
                  <FormatCell>
                    <FormatIcon format={d.format} />
                    {formatLabel[d.format]}
                  </FormatCell>
                </Td>
                <Td>
                  <StatusPill variant={statusVariant[d.status]}>
                    {statusLabel[d.status]}
                  </StatusPill>
                </Td>
                <Td>
                  <ActionCell>
                    <DeliveryRowActionsMenu
                      delivery={d}
                      isOpen={openMenuId === d.id}
                      onToggle={() => setOpenMenuId((id) => (id === d.id ? null : d.id))}
                      onClose={() => setOpenMenuId(null)}
                      onEdit={onEdit}
                      onPause={onPause}
                      onResume={onResume}
                      onCancel={onCancel}
                      onDelete={onDelete}
                    />
                  </ActionCell>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        {!isLoading && !isError && !isEmpty && (
          <Pagination
            page={page}
            pageCount={pageCount}
            pageSize={pageSize}
            totalCount={total}
            onPageChange={setPage}
            pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(1);
            }}
          />
        )}
      </TableWrap>
    </Card>
  );
};

export default ActiveDeliveriesTable;
